/**
 * 순찰 노트 관리 페이지
 * Patrol Note Management Page
 *
 * 전체 순찰 노트 조회, 삭제(연결 벌점 취소), 점수는 벌점 관리 순찰 항목 그대로 표시
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../../config/api';
import { authedFetch } from '../../../infrastructure/api/authedFetch';

// ==================== Types ====================

interface PatrolNote {
  id: string;
  seat_id: string;
  seat_code: string;
  student_id?: string;
  student_name?: string;
  note_type: string;
  note_type_label: string;
  note_text?: string;
  staff_id: string;
  staff_name?: string;
  star_effect: number;
  // ⭐ 2026-10-06 — 이 노트로 실제로 붙은 상벌점(벌점 관리 순찰 항목 점수). 벌점 음수·상점 양수, 없으면 null
  penalty_points?: number | null;
  penalty_status?: 'active' | 'cancelled' | null;
  penalty_reduced?: boolean;
  created_at: string;
}

interface ScoreSettings {
  focus: number;
  normal: number;
  sleep: number;
  phone: number;
  off_task: number;
  leave: number;
  return: number;
}

interface PatrolNotesResponse {
  success: boolean;
  data: {
    notes: PatrolNote[];
    total_count: number;
    page: number;
    limit: number;
    total_pages: number;
    stats: Record<string, number>;
    score_settings: ScoreSettings;
  };
}

// ==================== Component ====================

const PatrolNoteManagementPage: React.FC = () => {
  // 상태
  const [notes, setNotes] = useState<PatrolNote[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState<Record<string, number>>({});
  const [scoreSettings, setScoreSettings] = useState<ScoreSettings>({
    focus: 1, normal: 0, sleep: -1, phone: -1, off_task: -1, leave: 0, return: 1
  });

  // 필터 상태
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [noteType, setNoteType] = useState('');
  const [search, setSearch] = useState('');

  // UI 상태
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [editSettings, setEditSettings] = useState<ScoreSettings>(scoreSettings);
  // 2026-10-06 대표 결정 — 순찰 항목·점수는 [벌점 관리] → 설정의 '출석부 순찰 항목'이 유일한 기준.
  //   예전 '순찰 노트 점수 설정'(별점)은 이름이 고정돼 있고 실제 벌점에 반영되지 않아 원장을 헷갈리게 했다.
  //   이 창은 그 항목을 그대로 보여주고, 바꾸는 곳으로 바로 보낸다.
  type PatrolItem = { id: string; name: string; emoji?: string; points: number; point_type: string; notify_parent?: boolean };
  const [patrolItems, setPatrolItems] = useState<PatrolItem[] | null>(null);
  const [patrolItemsMsg, setPatrolItemsMsg] = useState('');   // 못 불러온 이유 (직원 계정 403 등)
  const openPatrolItems = async () => {
    setShowSettingsModal(true);
    setPatrolItems(null);
    setPatrolItemsMsg('');
    try {
      const r = await authedFetch(`${API_BASE_URL}/api/v1/owner/patrol-penalty-items`);
      if (r.ok) {
        setPatrolItems(await r.json());
      } else {
        // 순찰 항목은 원장 계정만 볼 수 있다 — '항목 없음'으로 잘못 보이지 않게 이유를 알린다
        setPatrolItemsMsg(r.status === 403 ? '순찰 항목은 원장 계정에서 확인·변경할 수 있습니다.' : '순찰 항목을 불러오지 못했습니다.');
        setPatrolItems([]);
      }
    } catch {
      setPatrolItemsMsg('순찰 항목을 불러오지 못했습니다.');
      setPatrolItems([]);
    }
  };
  const goPenaltySettings = () => {
    const base = window.location.pathname.startsWith('/studyroom/') ? '/studyroom/owner' : '/owner';
    window.location.href = `${base}/penalty?tab=settings#patrol-items`;
  };

  // API 호출
  const fetchPatrolNotes = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const params = new URLSearchParams();
      params.append('page', String(currentPage));
      params.append('limit', '20');
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (noteType) params.append('note_type', noteType);
      if (search) params.append('search', search);

      const response = await fetch(`${API_BASE_URL}/api/v1/owner/patrol-notes?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result: PatrolNotesResponse = await response.json();
        if (result.success) {
          setNotes(result.data.notes);
          setTotalCount(result.data.total_count);
          setTotalPages(result.data.total_pages);
          setStats(result.data.stats);
          setScoreSettings(result.data.score_settings);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || '순찰 노트를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('순찰 노트 조회 실패:', err);
      setError('순찰 노트를 가져오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, startDate, endDate, noteType, search]);

  // 노트 삭제 (연결된 벌점·상점 취소 포함)
  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('이 순찰 노트를 삭제하시겠습니까?\n이 순찰로 붙은 벌점(상점)도 함께 취소됩니다.')) {
      return;
    }

    try {
      setDeleteLoading(noteId);
      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');

      const response = await fetch(`${API_BASE_URL}/api/v1/owner/patrol-notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        alert(`순찰 노트가 삭제되었습니다.${result.penalty_cancelled ? '\n연결된 벌점(상점)도 취소되었습니다.' : ''}`);
        fetchPatrolNotes();
      } else {
        const errorData = await response.json();
        alert(errorData.detail || '삭제에 실패했습니다.');
      }
    } catch (err) {
      console.error('순찰 노트 삭제 실패:', err);
      alert('삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleteLoading(null);
    }
  };

  // 점수 설정 저장
  const handleSaveSettings = async () => {
    try {
      setSettingsLoading(true);
      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');

      const response = await fetch(`${API_BASE_URL}/api/v1/owner/patrol-notes/score-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editSettings)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setScoreSettings(result.data);
          setShowSettingsModal(false);
          alert('점수 설정이 저장되었습니다.');
        }
      } else {
        const errorData = await response.json();
        alert(errorData.detail || '설정 저장에 실패했습니다.');
      }
    } catch (err) {
      console.error('점수 설정 저장 실패:', err);
      alert('설정 저장 중 오류가 발생했습니다.');
    } finally {
      setSettingsLoading(false);
    }
  };

  // 데이터 로드
  useEffect(() => {
    fetchPatrolNotes();
  }, [fetchPatrolNotes]);

  // 필터 적용
  const handleApplyFilter = () => {
    setCurrentPage(1);
    fetchPatrolNotes();
  };

  // 필터 초기화
  const handleResetFilter = () => {
    setStartDate('');
    setEndDate('');
    setNoteType('');
    setSearch('');
    setCurrentPage(1);
  };

  // 날짜 포맷
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 점수 표시 — 벌점 관리에 실제로 남은 상벌점 그대로 (옛 '별점' 고정값 아님, 2026-10-06)
  const renderPenalty = (note: PatrolNote) => {
    const pts = note.penalty_points;
    if (pts === null || pts === undefined) {
      return <span className="text-gray-400">기록만</span>;
    }
    const label = pts > 0 ? `상점 +${pts}` : pts < 0 ? `벌점 ${pts}` : '0점';
    if (note.penalty_status === 'cancelled') {
      return (
        <span className="text-gray-400">
          <span className="line-through">{label}</span> 취소
        </span>
      );
    }
    return (
      <span className={pts > 0 ? 'text-green-600 font-semibold' : pts < 0 ? 'text-red-600 font-semibold' : 'text-gray-500'}>
        {label}
        {note.penalty_reduced && <span className="ml-1 text-xs font-normal text-gray-500">(감경)</span>}
      </span>
    );
  };

  // 노트 타입 옵션
  const noteTypeOptions = [
    { value: '', label: '전체' },
    { value: 'focus', label: '집중' },
    { value: 'normal', label: '양호' },
    { value: 'sleep', label: '졸음' },
    { value: 'phone', label: '휴대폰' },
    { value: 'off_task', label: '잡담' },
    { value: 'leave', label: '자리비움' },
    { value: 'return', label: '복귀' },
    { value: 'bathroom', label: '화장실' },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* 헤더 */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-900">순찰 노트 관리</h1>
        <button
          onClick={openPatrolItems}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          순찰 항목·점수
        </button>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
          <div className="text-sm text-gray-500">전체</div>
        </div>
        <div className="bg-green-50 rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{stats.focus || 0}</div>
          <div className="text-sm text-green-600">집중</div>
        </div>
        <div className="bg-gray-50 rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-600">{stats.normal || 0}</div>
          <div className="text-sm text-gray-500">양호</div>
        </div>
        <div className="bg-red-50 rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{stats.sleep || 0}</div>
          <div className="text-sm text-red-600">졸음</div>
        </div>
        <div className="bg-red-50 rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{stats.phone || 0}</div>
          <div className="text-sm text-red-600">휴대폰</div>
        </div>
        <div className="bg-red-50 rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{stats.off_task || 0}</div>
          <div className="text-sm text-red-600">잡담</div>
        </div>
        <div className="bg-blue-50 rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{stats.return || 0}</div>
          <div className="text-sm text-blue-600">복귀</div>
        </div>
      </div>

      {/* 필터 */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">시작일</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">종료일</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">노트 유형</label>
            <select
              value={noteType}
              onChange={(e) => setNoteType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {noteTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">학생 검색</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="학생 이름"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleApplyFilter}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              검색
            </button>
            <button
              onClick={handleResetFilter}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              초기화
            </button>
          </div>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* 테이블 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">로딩 중...</div>
        ) : notes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">순찰 노트가 없습니다.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">날짜</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">학생</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">좌석</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">유형</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">내용</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">점수</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">작성자</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">삭제</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {notes.map((note) => (
                  <tr key={note.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(note.created_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {note.student_name || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {note.seat_code}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        note.penalty_status === 'active' && (note.penalty_points ?? 0) > 0 ? 'bg-green-100 text-green-800' :
                        note.penalty_status === 'active' && (note.penalty_points ?? 0) < 0 ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {note.note_type_label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                      {note.note_text || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {renderPenalty(note)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {note.staff_name || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        disabled={deleteLoading === note.id}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50"
                        title="삭제 (연결된 벌점·상점도 취소)"
                      >
                        {deleteLoading === note.id ? (
                          <span className="animate-spin">...</span>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              총 {totalCount}건 중 {(currentPage - 1) * 20 + 1} - {Math.min(currentPage * 20, totalCount)}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50"
              >
                이전
              </button>
              <span className="px-3 py-1 text-sm text-gray-600">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 순찰 항목·점수 — 벌점 관리의 '출석부 순찰 항목'을 그대로 보여준다 (2026-10-06) */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">순찰 항목 · 점수</h2>
              <button onClick={() => setShowSettingsModal(false)} className="text-gray-400 hover:text-gray-600" aria-label="닫기">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-3">
                순찰 태블릿·출석부에서 표시하는 항목과 점수입니다. 표시하면 이 점수대로 벌점(상점)이 붙습니다.
              </p>
              {patrolItems === null ? (
                <div className="text-sm text-gray-400 py-4 text-center">불러오는 중…</div>
              ) : patrolItems.length === 0 ? (
                <div className="text-sm text-gray-500 py-4 text-center">{patrolItemsMsg || '등록된 순찰 항목이 없습니다.'}</div>
              ) : (
                <div className="space-y-2">
                  {patrolItems.map((it) => (
                    <div key={it.id} className="flex items-center justify-between border rounded-lg px-3 py-2">
                      <span className="text-gray-800">{it.emoji} {it.name}</span>
                      <span className="flex items-center gap-1">
                        {it.notify_parent && (
                          <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700" title="표시하면 학부모 알림톡 발송">📨 알림</span>
                        )}
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          it.points === 0 ? 'bg-gray-100 text-gray-600'
                            : it.point_type === 'merit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {it.points === 0 ? '기록만' : `${it.point_type === 'merit' ? '상점 +' : '벌점 -'}${it.points}점`}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-3">
                이름·점수·학부모 알림은 [벌점 관리] → 설정 → &apos;출석부 순찰 항목 관리&apos;에서 바꿉니다.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowSettingsModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                닫기
              </button>
              <button onClick={goPenaltySettings} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                벌점 관리에서 변경하기 →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatrolNoteManagementPage;
